'use client'

import { useState } from 'react'
import { identifyNewsletterSubscriber } from '@/lib/analytics/posthog'

/**
 * Shared submit logic for every Klaviyo signup form on the site (newsletter
 * block, Events header). One place so the route contract, PostHog identify and
 * the status-reset timing can't drift between forms.
 *
 * Also owns the honeypot value: render the returned <HoneypotField> inside the
 * form and the bot check in /api/newsletter/subscribe works for free.
 */
export function useNewsletterSubscribe(audienceType: string) {
  const [email, setEmail] = useState('')
  const [honeypot, setHoneypot] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!email) return

    setIsSubmitting(true)

    try {
      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, audienceType, fax: honeypot }),
      })

      if (!response.ok) {
        throw new Error('Subscription failed')
      }

      const submittedEmail = email
      setStatus('success')
      identifyNewsletterSubscriber(submittedEmail)
      setEmail('')

      // Reset success message after 5 seconds
      setTimeout(() => {
        setStatus('idle')
      }, 5000)
    } catch (error) {
      console.error('Newsletter subscription error:', error)
      setStatus('error')

      // Reset error message after 5 seconds
      setTimeout(() => {
        setStatus('idle')
      }, 5000)
    } finally {
      setIsSubmitting(false)
    }
  }

  return { email, setEmail, honeypot, setHoneypot, isSubmitting, status, handleSubmit }
}
